import { useTranslation } from 'react-i18next';
import Page from '../../../../core/components/Page';
import classnames from '../../../../core/utils/classnames.tsx';
import { canManageChannels } from '../../../../users/utils';
import { useApplicationContext } from '../../../../core/contexts/ApplicationContext';
import DropdownMenu from '../../../../core/components/DropdownMenu';
import { Link } from 'react-router-dom';
import { url } from '../../../../core/utils/link';
import * as React from 'react';
import { useOverviewContext } from './OverviewContext';
import { withIcon } from '../../../../core/components/Icon';
import {
    faArrowToTop,
    faArrowUpRightFromSquare,
    faEllipsisVertical,
    faPlus,
    faSpinnerThird,
} from '@fortawesome/pro-regular-svg-icons';
import DeleteChannelModal from './DeleteChannelModal.tsx';
import { DocumentType } from '../../../constants.ts';
import { ANALYTICS_DASHBOARD_PATH } from '../../../../analytics/constants.ts';

const AddIcon = withIcon(faPlus);
const UploadIcon = withIcon(faArrowToTop);
const MoreIcon = withIcon(faEllipsisVertical);
const LoadingIcon = withIcon(faSpinnerThird);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);

export const AllChannelsHeader = ({ user, onAddChannelVisibleChange, documentConfiguration }) => {
    const { t } = useTranslation('assistance');

    const documentType = documentConfiguration?.documentTypeName;

    return (
        <Page.Header className="justify-between">
            <Page.HeaderTitle>{t('overview.navigation.allChannels')}</Page.HeaderTitle>

            <div className="flex gap-2">
                <Page.HeaderButton
                    className={classnames(!canManageChannels(user) && 'menu-item--disabled')}
                    onClick={() => onAddChannelVisibleChange(true)}
                >
                    <AddIcon />
                    {t('overview.header.actions.addChannel')}
                </Page.HeaderButton>

                <DropdownMenu>
                    <DropdownMenu.Trigger asChild>
                        <Page.HeaderIconButton>
                            <MoreIcon />
                        </Page.HeaderIconButton>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end">
                        <DropdownMenu.Item asChild>
                            <Link
                                to={url(ANALYTICS_DASHBOARD_PATH, undefined, {
                                    search: { document_type: documentType },
                                })}
                                target="_blank"
                                className="flex items-center gap-4"
                            >
                                {t('overview.header.actions.showAllAnalytics')}
                                <span className="text-xs">
                                    <ExternalLinkIcon />
                                </span>
                            </Link>
                        </DropdownMenu.Item>
                    </DropdownMenu.Content>
                </DropdownMenu>
            </div>
        </Page.Header>
    );
};

export const SingleChannelHeader = ({
    activeChannel,
    onCopyEmailAddress,
    onUploadFileModalVisibleChange,
    onDuplicateChannelVisibleChange,
    documentConfiguration,
}) => {
    const { t } = useTranslation('assistance');

    const { user } = useApplicationContext();
    const { channelId } = useOverviewContext();
    const [deleteVisible, setDeleteVisible] = React.useState(false);
    const canDeleteChannel =
        user.canSwitchSupportUser && documentConfiguration.documentType !== DocumentType.PropertyBill;

    const documentType = documentConfiguration?.documentTypeName;

    return (
        <Page.Header className="justify-between">
            <div className="flex items-center gap-2">
                <Page.HeaderTitle>
                    {activeChannel?.name || <LoadingIcon spin className="text-content-brand-strong" />}
                </Page.HeaderTitle>
            </div>

            <div className="flex gap-2">
                <DropdownMenu>
                    <DropdownMenu.Trigger asChild>
                        <Page.HeaderButton>
                            <UploadIcon />
                            {t('overview.header.actions.uploadFile')}
                        </Page.HeaderButton>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end">
                        <DropdownMenu.Item
                            onClick={() => onUploadFileModalVisibleChange(true)}
                            disabled={!user.customer?.isActive}
                        >
                            {t('overview.header.actions.uploadFileComputer')}
                        </DropdownMenu.Item>

                        <DropdownMenu.Item
                            onClick={() => onCopyEmailAddress(activeChannel.emailAddress)}
                            className="flex flex-col items-start"
                            disabled={!activeChannel?.emailAddress}
                        >
                            <span>{t('overview.header.actions.copyEmailAddress')}</span>
                            <span className="text-xs text-content-weak">
                                {activeChannel?.emailAddress?.length > 32
                                    ? activeChannel?.emailAddress.slice(0, 29) + '...'
                                    : activeChannel?.emailAddress}
                            </span>
                        </DropdownMenu.Item>
                    </DropdownMenu.Content>
                </DropdownMenu>

                <DropdownMenu>
                    <DropdownMenu.Trigger asChild>
                        <Page.HeaderIconButton>
                            <MoreIcon />
                        </Page.HeaderIconButton>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end">
                        <DropdownMenu.Item asChild>
                            <Link
                                to={url(ANALYTICS_DASHBOARD_PATH, undefined, {
                                    search: { document_type: documentType, channel: activeChannel?.id },
                                })}
                                target="_blank"
                                className="flex items-center gap-4"
                            >
                                {t('overview.header.actions.showChannelAnalytics')}
                                <span className="text-xs">
                                    <ExternalLinkIcon />
                                </span>
                            </Link>
                        </DropdownMenu.Item>

                        <DropdownMenu.Separator />

                        <DropdownMenu.Item
                            disabled={!canManageChannels(user)}
                            onClick={() => onDuplicateChannelVisibleChange(true)}
                        >
                            {t('overview.header.actions.duplicateChannel')}
                        </DropdownMenu.Item>

                        <DropdownMenu.Item disabled={!canManageChannels(user)} asChild>
                            <Link to={url(documentConfiguration.constants.CHANNEL_CONFIG_PATH, { channelId })}>
                                {t('overview.header.actions.manageChannel')}
                            </Link>
                        </DropdownMenu.Item>

                        <DropdownMenu.Item asChild>
                            <Link to={url(documentConfiguration.constants.CHANNEL_MASTERDATA_PATH, { channelId })}>
                                {t('overview.header.actions.manageMasterdata')}
                            </Link>
                        </DropdownMenu.Item>
                        {canDeleteChannel && (
                            <>
                                <DropdownMenu.Separator />
                                <DropdownMenu.Item
                                    className="text-content-danger-strong"
                                    disabled={!activeChannel?.id}
                                    onClick={() => setDeleteVisible(true)}
                                >
                                    {t('overview.deleteChannelModal.title')}
                                </DropdownMenu.Item>
                            </>
                        )}
                    </DropdownMenu.Content>
                </DropdownMenu>
            </div>
            {deleteVisible && activeChannel && (
                <DeleteChannelModal channel={activeChannel} onClose={() => setDeleteVisible(false)} />
            )}
        </Page.Header>
    );
};
